import { useEffect } from 'react';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';
import { Toast, useToastStore } from '../store/toastStore';

const typeStyles: Record<
  Toast['type'],
  { wrapper: string; icon: typeof CheckCircle }
> = {
  success: {
    wrapper: 'border-green-200 bg-green-50 text-green-900',
    icon: CheckCircle,
  },
  error: {
    wrapper: 'border-red-200 bg-red-50 text-red-900',
    icon: AlertCircle,
  },
  info: {
    wrapper: 'border-blue-200 bg-blue-50 text-blue-900',
    icon: Info,
  },
};

const DEFAULT_DURATION = 4000;

function ToastMessage({ toast }: { toast: Toast }) {
  const removeToast = useToastStore((state) => state.removeToast);
  const Icon = typeStyles[toast.type].icon;

  useEffect(() => {
    const duration = toast.duration ?? DEFAULT_DURATION;
    if (duration === 0) {
      return;
    }

    const timer = window.setTimeout(() => removeToast(toast.id), duration);

    return () => {
      clearTimeout(timer);
    };
  }, [toast, removeToast]);

  return (
    <div className={`pointer-events-auto flex min-w-[280px] items-start gap-3 rounded-lg border px-4 py-3 shadow-lg ${typeStyles[toast.type].wrapper}`}>
      <Icon className="mt-0.5 h-4 w-4" />
      <div className="flex-1">
        {toast.title && <p className="text-sm font-semibold">{toast.title}</p>}
        {toast.description && <p className="text-sm text-current">{toast.description}</p>}
      </div>
      <button
        type="button"
        aria-label="Fechar notificação"
        onClick={() => removeToast(toast.id)}
        className="ml-2 rounded p-1 opacity-70 transition hover:bg-black/5 hover:opacity-100"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((state) => state.toasts);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      role="region"
      aria-live="polite"
      className="pointer-events-none fixed top-4 right-4 z-50 flex flex-col gap-3"
    >
      {toasts.map((toast) => (
        <ToastMessage key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
